import React from 'react';
import { getHeaderCheckedStatus } from './libs/basic';
import { RenderRowSelection } from './components/row-selection';
import { ITableResizeConfig, ITableRowSelection } from './model/table';
import {
  ITableColumn,
  ITableSortValue,
  ITableHeader,
  IPagination,
  IString,
  CollapseColumnRenderType,
} from './model';
import {
  addEmptyBody,
  ColumnSortComponent,
  FilterComponent,
  removeIndexCache,
  TableHeaderColumnComponent,
} from './components';
import classNames from 'classnames';
import { INumber } from '../../model';
import { isColumnCollapse } from './libs';

interface IProps<T> {
  readonly columns: ReadonlyArray<ITableColumn<T>>;
  readonly data: ReadonlyArray<T>;
  readonly fullData: ReadonlyArray<T>;
  readonly rowSelection?: ITableRowSelection<T>;
  readonly rowKey: string;
  readonly pagination?: IPagination;
  readonly onHeaderRow?: (
    columns: ReadonlyArray<ITableColumn<T>>,
    index: number,
    column: ITableColumn<T>
  ) => ITableHeader;

  // 排序
  readonly sortValue: ITableSortValue | null;
  readonly setSortValue: (
    value: ITableSortValue | null,
    item: ITableColumn<T>
  ) => void;
  readonly sortLoading: boolean;

  // 过滤项
  readonly filterValues: IString<string | string[]>;
  readonly onChangedFilterValues: (
    key: string,
    value: string | string[]
  ) => void;

  readonly children?: React.ReactNode;
  readonly isStickyHeader?: boolean;
  readonly isEnableResize: boolean;
  readonly resizeConfig: ITableResizeConfig;
  readonly onWidthChanged: (index: number, width: number) => void;
  readonly columnCollapseIndex: INumber<ReadonlyArray<number>>;
  readonly tableRefElement?: HTMLTableElement;
}

export function TableHeader<T>(props: IProps<T>) {
  const renderColumnChildren = (item: ITableColumn<T>, index: number) => {
    const sortNode = item.sortable ? (
      <ColumnSortComponent<T>
        columnIndex={index}
        column={item}
        sorter={item.sorter}
        sortValue={props.sortValue}
        sortable={item.sortable}
        setSortValue={props.setSortValue}
        sortLoading={props.sortLoading}
      />
    ) : null;

    const filterNode = item.filters ? (
      <FilterComponent<T>
        columnIndex={index}
        columnKey={item.key}
        filters={item.filters}
        filterValues={props.filterValues}
        onChangedFilterValues={props.onChangedFilterValues}
      />
    ) : null;

    return (
      <>
        {filterNode}
        {sortNode}
      </>
    );
  };

  const renderTH = () => {
    removeIndexCache(CollapseColumnRenderType.Header);
    return props.columns.map((item, index) => {
      const { collapsed, collapsedIndex } = isColumnCollapse(
        props.columnCollapseIndex,
        index + 1
      );
      if (collapsed) {
        return addEmptyBody(CollapseColumnRenderType.Header, collapsedIndex);
      }
      return (
        <TableHeaderColumnComponent
          columns={props.columns}
          data={props.data}
          pagination={props.pagination}
          onHeaderRow={props.onHeaderRow}
          item={item}
          index={index}
          key={index}
          sortChildren={renderColumnChildren(item, index)}
          isEnableResize={props.isEnableResize}
          resizeConfig={props.resizeConfig}
          onWidthChanged={props.onWidthChanged}
          rowSelection={props.rowSelection}
          tableRefElement={props.tableRefElement}
        />
      );
    });
  };

  const renderRowSelection = () => {
    if (props.rowSelection) {
      return (
        <RenderRowSelection<T>
          rowSelection={props.rowSelection}
          rowIndex={-1}
          checked={getHeaderCheckedStatus(
            props.rowSelection,
            props.fullData.length
          )}
          data={props.data}
          fullData={props.fullData}
          rowKey={props.rowKey}
        />
      );
    }
  };

  const className = classNames(props.isStickyHeader ? 'sticky-header' : '');

  return (
    <thead className={className}>
      {props.children}
      <tr>
        {renderRowSelection()}
        {renderTH()}
      </tr>
    </thead>
  );
}
